<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  FIRST_PUBLISHED_AT,
  OLDEST_CALENDAR_DATE,
  SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT,
  searchPresetDates,
  userDocumentSearchPresetDates,
} from '@/constants';
import SearchMenuAll from '@/components/menu/search-menu/search-menu-all.vue';
import SearchMenuCheckbox from '@/components/menu/search-menu/search-menu-checkbox.vue';
import SearchMenuDropdownCalendar from '@/components/menu/search-menu/search-menu-dropdown-calendar.vue';
import SearchMenuNews from '@/components/menu/search-menu/search-menu-news.vue';
import { Lang, SearchCondition, SearchConditionKey, SearchDate } from '@/types';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import { getToday } from '@/utils/dates';
import {
  ALLIANCE_MEDIA_VALUE,
  SearchSiteCategory,
  SITE_CATEGORY_ITEMS,
  SiteCategoryItems,
} from '@/utils/siteCategories';
import { useStore } from '@/utils/vue';

// ヘッダー(68px),絞り込みタイトル(64px),開閉ボタン(40px)を引いた高さに設定
const height = 'calc(100vh - (68px + 64px + 40px))';

const isNextLayout = useIsNextLayout();

const store = useStore();
const route = useRoute();

const teamInfo = computed(() => store.state.teamInfo.teamInfo);

const searchConditionKey = computed<SearchConditionKey>(() => {
  if (route.name === 'searchAll') return 'all';
  if (route.name === 'searchNews') return 'news';
  if (route.name === 'searchResearchPapers') return 'research_paper';
  if (route.name === 'searchPatent') return 'patent';
  if (route.name === 'searchReport') return 'report';
  if (route.name === 'searchUserDocument') return 'user_document';
  if (route.name === 'searchUserDocumentDemo') return 'user_document_demo';
  return 'all';
});

const searchCondition = computed<SearchCondition>(() => {
  return store.getters['searchResults/searchCondition'](
    searchConditionKey.value,
  );
});

const isOpenSiteCategories = ref(true);

const siteCategoryItems = computed<SiteCategoryItems[]>(() =>
  teamInfo.value?.enable_alliance_media
    ? SITE_CATEGORY_ITEMS
    : SITE_CATEGORY_ITEMS.filter(c => c.value !== ALLIANCE_MEDIA_VALUE),
);

const checkedSiteCategoryItems = computed<string[]>(() =>
  teamInfo.value?.enable_alliance_media
    ? searchCondition.value['site_categories']
    : searchCondition.value['site_categories'].filter(
        c => c !== ALLIANCE_MEDIA_VALUE,
      ),
);

const removeAllSiteCategory = () => {
  store.commit('searchResults/setSiteCategoryCondition', {
    key: searchConditionKey.value,
    value: [],
  });
};

const addSiteCategory = (value: string) => {
  const siteCategories = searchCondition.value['site_categories'].slice();
  siteCategories.push(value as SearchSiteCategory);
  store.commit('searchResults/setSiteCategoryCondition', {
    key: searchConditionKey.value,
    value: siteCategories,
  });
};

const removeSiteCategory = (value: string) => {
  const siteCategories = searchCondition.value['site_categories'].slice();
  store.commit('searchResults/setSiteCategoryCondition', {
    key: searchConditionKey.value,
    value: siteCategories.filter(item => item !== value),
  });
};

// メディア種別
const isMediaTypeVisible = computed(() => {
  return (
    teamInfo.value?.enable_foreign_lang_media &&
    searchConditionKey.value !== 'report'
  );
});
const isOpenMediaType = ref(true);
const mediaTypeItems = ref([
  { label: '国内', value: 'ja' },
  { label: '海外', value: 'en' },
]);

const checkedMediaTypeItems = computed<string[]>(() => {
  return searchCondition.value['lang'];
});

const addAllMediaType = () => {
  store.commit('searchResults/setLanguageCondition', {
    key: searchConditionKey.value,
    value: ['ja', 'en'],
  });
};

const removeAllMediaType = () => {
  store.commit('searchResults/setLanguageCondition', {
    key: searchConditionKey.value,
    value: [],
  });
};

const addMediaType = (value: string) => {
  const lang = searchCondition.value['lang'].slice();
  lang.push(value as Lang);
  store.commit('searchResults/setLanguageCondition', {
    key: searchConditionKey.value,
    value: lang,
  });
};

const removeMediaType = (value: string) => {
  const lang = searchCondition.value['lang'].slice();
  store.commit('searchResults/setLanguageCondition', {
    key: searchConditionKey.value,
    value: lang.filter(item => item !== value),
  });
};

// 期間
const isOpenDropDownCalendar = ref(true);
const TODAY = ref(getToday());

const startDate = computed(() => {
  return searchCondition.value['from'];
});
const endDate = computed(() => {
  return searchCondition.value['to'];
});

const applyDate = (option: {
  presetKey: string;
  value: { from: SearchDate; to: SearchDate };
}) => {
  store.dispatch('searchResults/updateSearchCondition', {
    key: searchConditionKey.value,
    value: {
      ...searchCondition.value,
      period: option.presetKey,
      from: option.value.from,
      to: option.value.to,
      news_categories: [],
    },
  });
};

// エンタメフィルタ
const isEntertainmentFilter = computed(() => {
  return searchCondition.value['is_entertainment_sites_filtered'];
});

const toggleEntertainmentFilter = () => {
  store.dispatch('searchResults/updateSearchCondition', {
    key: searchConditionKey.value,
    value: {
      ...searchCondition.value,
      is_entertainment_sites_filtered:
        !searchCondition.value.is_entertainment_sites_filtered,
      news_categories: [],
    },
  });
};

// 社内情報更新者
const isOpenUserDocumentUpdater = ref(true);

const userDocumentUpdaterOptionItems = computed(() => {
  return store.state.searchResults.lastUpdatedBy.map(updater => {
    return { label: updater, value: updater };
  });
});

const checkedUserDocumentUpdaterItems = computed(
  () => searchCondition.value['last_updated_by'],
);

const addUserDocumentUpdater = (value: string) => {
  if (!searchCondition.value['last_updated_by']) return;
  const lastUpdatedBy = searchCondition.value['last_updated_by'].slice();
  lastUpdatedBy.push(value);
  store.commit('searchResults/setLastUpdatedByCondition', {
    key: searchConditionKey.value,
    value: lastUpdatedBy,
  });
};

const removeUserDocumentUpdater = (value: string) => {
  if (!searchCondition.value['last_updated_by']) return;
  const lastUpdatedBy = searchCondition.value['last_updated_by'].slice();
  store.commit('searchResults/setLastUpdatedByCondition', {
    key: searchConditionKey.value,
    value: lastUpdatedBy.filter(item => item !== value),
  });
};

const removeAllUserDocumentUpdater = () =>
  store.commit('searchResults/setLastUpdatedByCondition', {
    key: searchConditionKey.value,
    value: [],
  });

const minDate = computed(() => {
  if (searchConditionKey.value in SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT) {
    return SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT[
      searchConditionKey.value as keyof typeof SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT
    ];
  }
  return FIRST_PUBLISHED_AT;
});

// 特許の公開庁
const isPatentPublicationOfficeVisible = computed(() => {
  return (
    teamInfo.value?.enable_technical_literature &&
    ['all', 'patent'].includes(searchConditionKey.value)
  );
});
const isOpenPatentPublicationOffice = ref(true);
const ALL_PATENT_OFFICE_ITEMS = [
  { label: 'JPO', value: 'jpo' },
  { label: 'WIPO', value: 'wipo' },
  { label: 'USPTO', value: 'uspto' },
];
const patentPublicationOfficeItems = computed(() =>
  ALL_PATENT_OFFICE_ITEMS.filter(item =>
    // USPTOは海外のみなので海外メディアが無効の時に非表示する
    teamInfo.value?.enable_foreign_lang_media ? true : item.value !== 'uspto',
  ),
);

const checkedPatentPublicationOfficeItems = computed<string[]>(() => {
  return searchCondition.value['patent_publication_offices'];
});

const addAllPatentPublicationOffice = () => {
  store.commit('searchResults/setPatentPublicationOfficeCondition', {
    key: searchConditionKey.value,
    value: patentPublicationOfficeItems.value.map(i => i.value),
  });
};

const removeAllPatentPublicationOffice = () => {
  store.commit('searchResults/setPatentPublicationOfficeCondition', {
    key: searchConditionKey.value,
    value: [],
  });
};

const addPatentPublicationOffice = (value: string) => {
  const patentPublicationOffice =
    searchCondition.value['patent_publication_offices'].slice();
  patentPublicationOffice.push(value);
  store.commit('searchResults/setPatentPublicationOfficeCondition', {
    key: searchConditionKey.value,
    value: patentPublicationOffice,
  });
};

const removePatentPublicationOffice = (value: string) => {
  const patentPublicationOffice =
    searchCondition.value['patent_publication_offices'].slice();
  store.commit('searchResults/setPatentPublicationOfficeCondition', {
    key: searchConditionKey.value,
    value: patentPublicationOffice.filter(item => item !== value),
  });
};
</script>

<template>
  <div class="search-menu" :class="{ 'is-next-layout': isNextLayout }">
    <div class="title">
      <span class="c-title-bold--m">絞り込み</span>
    </div>
    <div class="search-conditions">
      <template v-if="route.name === 'searchAll'">
        <SearchMenuAll
          :is-media-type-visible="isMediaTypeVisible"
          :is-open-media-type="isOpenMediaType"
          :media-type-items="mediaTypeItems"
          :checked-media-type-items="checkedMediaTypeItems"
          :is-open-drop-down-calendar="isOpenDropDownCalendar"
          :start-date="startDate"
          :end-date="endDate"
          :is-open-site-categories="isOpenSiteCategories"
          :site-category-items="siteCategoryItems"
          :checked-site-category-items="checkedSiteCategoryItems"
          :is-entertainment-filter="isEntertainmentFilter"
          :is-patent-publication-office-visible="
            isPatentPublicationOfficeVisible
          "
          :is-open-patent-publication-office="isOpenPatentPublicationOffice"
          :patent-publication-office-items="patentPublicationOfficeItems"
          :checked-patent-publication-office-items="
            checkedPatentPublicationOfficeItems
          "
          @open-media-type="isOpenMediaType = !isOpenMediaType"
          @add-all-media-type="addAllMediaType"
          @remove-all-media-type="removeAllMediaType"
          @add-media-type="addMediaType"
          @remove-media-type="removeMediaType"
          @open-drop-down-calendar="
            isOpenDropDownCalendar = !isOpenDropDownCalendar
          "
          @apply-date="applyDate"
          @open-site-categories="isOpenSiteCategories = !isOpenSiteCategories"
          @remove-all-site-category="removeAllSiteCategory"
          @add-site-category="addSiteCategory"
          @remove-site-category="removeSiteCategory"
          @toggle-entertainment-filter="toggleEntertainmentFilter"
          @open-patent-publication-office="
            isOpenPatentPublicationOffice = !isOpenPatentPublicationOffice
          "
          @add-all-patent-publication-office="addAllPatentPublicationOffice"
          @remove-all-patent-publication-office="
            removeAllPatentPublicationOffice
          "
          @add-patent-publication-office="addPatentPublicationOffice"
          @remove-patent-publication-office="removePatentPublicationOffice"
        />
      </template>
      <template v-else-if="route.name === 'searchNews'">
        <SearchMenuNews
          :is-open-site-categories="isOpenSiteCategories"
          :site-category-items="siteCategoryItems"
          :checked-site-category-items="checkedSiteCategoryItems"
          :is-media-type-visible="isMediaTypeVisible"
          :is-open-media-type="isOpenMediaType"
          :media-type-items="mediaTypeItems"
          :checked-media-type-items="checkedMediaTypeItems"
          :is-open-drop-down-calendar="isOpenDropDownCalendar"
          :start-date="startDate"
          :end-date="endDate"
          :is-entertainment-filter="isEntertainmentFilter"
          @open-site-categories="isOpenSiteCategories = !isOpenSiteCategories"
          @remove-all-site-category="removeAllSiteCategory"
          @add-site-category="addSiteCategory"
          @remove-site-category="removeSiteCategory"
          @open-media-type="isOpenMediaType = !isOpenMediaType"
          @add-all-media-type="addAllMediaType"
          @remove-all-media-type="removeAllMediaType"
          @add-media-type="addMediaType"
          @remove-media-type="removeMediaType"
          @open-drop-down-calendar="
            isOpenDropDownCalendar = !isOpenDropDownCalendar
          "
          @apply-date="applyDate"
          @toggle-entertainment-filter="toggleEntertainmentFilter"
          @open-patent-publication-office="
            isOpenPatentPublicationOffice = !isOpenPatentPublicationOffice
          "
          @add-all-patent-publication-office="addAllPatentPublicationOffice"
          @remove-all-patent-publication-office="
            removeAllPatentPublicationOffice
          "
          @add-patent-publication-office="addPatentPublicationOffice"
          @remove-patent-publication-office="removePatentPublicationOffice"
        />
      </template>
      <template v-else-if="route.name === 'searchUserDocument'">
        <SearchMenuDropdownCalendar
          title="期間"
          :is-open="isOpenDropDownCalendar"
          :range="{
            from: startDate,
            to: endDate,
          }"
          :preset="userDocumentSearchPresetDates"
          :min="OLDEST_CALENDAR_DATE"
          :max="TODAY"
          :disabled="false"
          @toggle-is-open="isOpenDropDownCalendar = !isOpenDropDownCalendar"
          @apply-date="applyDate"
        ></SearchMenuDropdownCalendar>
        <SearchMenuCheckbox
          title="更新者"
          :items="userDocumentUpdaterOptionItems"
          :checked-values="checkedUserDocumentUpdaterItems || []"
          :is-open="isOpenUserDocumentUpdater"
          @toggle-is-open="
            isOpenUserDocumentUpdater = !isOpenUserDocumentUpdater
          "
          @un-checked-all-item="removeAllUserDocumentUpdater"
          @checked-item="addUserDocumentUpdater"
          @un-checked-item="removeUserDocumentUpdater"
        ></SearchMenuCheckbox>
      </template>
      <template v-else-if="route.name === 'searchUserDocumentDemo'">
        <SearchMenuDropdownCalendar
          title="期間"
          :is-open="isOpenDropDownCalendar"
          :range="{
            from: startDate,
            to: endDate,
          }"
          :preset="userDocumentSearchPresetDates"
          :min="OLDEST_CALENDAR_DATE"
          :max="TODAY"
          :disabled="true"
          @toggle-is-open="isOpenDropDownCalendar = !isOpenDropDownCalendar"
          @apply-date="applyDate"
        ></SearchMenuDropdownCalendar>
        <SearchMenuCheckbox
          title="更新者"
          :items="userDocumentUpdaterOptionItems"
          :checked-values="checkedUserDocumentUpdaterItems || []"
          :is-open="isOpenUserDocumentUpdater"
          :disabled="true"
          @toggle-is-open="
            isOpenUserDocumentUpdater = !isOpenUserDocumentUpdater
          "
          @un-checked-all-item="removeAllUserDocumentUpdater"
          @checked-item="addUserDocumentUpdater"
          @un-checked-item="removeUserDocumentUpdater"
        ></SearchMenuCheckbox>
      </template>
      <template v-else>
        <SearchMenuDropdownCalendar
          title="期間"
          :is-open="isOpenDropDownCalendar"
          :range="{
            from: startDate,
            to: endDate,
          }"
          :preset="searchPresetDates"
          :min="minDate"
          :max="TODAY"
          :disabled="false"
          @toggle-is-open="isOpenDropDownCalendar = !isOpenDropDownCalendar"
          @apply-date="applyDate"
        />
        <SearchMenuCheckbox
          v-if="isMediaTypeVisible"
          title="メディア種別"
          :items="mediaTypeItems"
          :checked-values="checkedMediaTypeItems"
          :is-open="isOpenMediaType"
          @toggle-is-open="isOpenMediaType = !isOpenMediaType"
          @checked-all-item="addAllMediaType"
          @un-checked-all-item="removeAllMediaType"
          @checked-item="addMediaType"
          @un-checked-item="removeMediaType"
        />
        <SearchMenuCheckbox
          v-if="isPatentPublicationOfficeVisible"
          title="公開庁"
          :items="patentPublicationOfficeItems"
          :checked-values="checkedPatentPublicationOfficeItems"
          :is-open="isOpenPatentPublicationOffice"
          @toggle-is-open="
            isOpenPatentPublicationOffice = !isOpenPatentPublicationOffice
          "
          @checked-all-item="addAllPatentPublicationOffice"
          @un-checked-all-item="removeAllPatentPublicationOffice"
          @checked-item="addPatentPublicationOffice"
          @un-checked-item="removePatentPublicationOffice"
        />
        <div class="divider" />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.search-menu {
  width: 290px;

  .title {
    padding-left: 16px;
    height: 72px;
    display: flex;
    align-items: center;
  }

  .search-conditions {
    height: v-bind(height);
    overflow-y: scroll;
  }

  .divider {
    border-top: solid 1px $color-gray400;
  }

  &.is-next-layout {
    width: 100%;

    .title {
      position: sticky;
      top: 0;
      background: $color-white;
      z-index: var(--z-content-header);
    }

    .search-conditions {
      height: auto;
      overflow: visible;
      padding-bottom: 64px; // メニュー折りたたみボタンに被らないため
    }
  }
}
</style>
